import React, { useState, useEffect } from 'react';
import {
    User,
    LogIn,
    Download,
    Wallet,
    Info,
    Shield,
    FileText,
    Users,
    XCircle,
    BookOpen,
    HelpCircle,
    Phone,
    Edit,
    Contact,
    Calendar,
    MapPin,
    CheckCircle,
    ArrowRight,
    Heart,
    LogOut as LogOutIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import DefaultSEO from '../components/DefaultSEO';

const AccountDetailPage = () => {

    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userProfile, setUserProfile] = useState({
        name: 'User',
        email: 'N/A',
        phone: 'N/A',
        joinDate: 'N/A',
        photoURL: null
    });

    // Real dashboard data from API
    const [dashboardStats, setDashboardStats] = useState({
        walletBalance: 0,
        totalBookings: 0,
        activeBookings: 0
    });

    const [upcomingBooking, setUpcomingBooking] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [imageError, setImageError] = useState(false);

    // Handle authentication state and profile updates
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            setIsLoggedIn(false);
            navigate('/login', { replace: true });
            return;
        }

        setIsLoggedIn(true);

        // Fetch user profile from backend API
        const fetchProfile = async () => {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            try {
                const res = await fetch(`${apiBase}/auth/me`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.success && data.data) {
                        const u = data.data;
                        setUserProfile({
                            name: u.name || 'Zoyo User',
                            email: (u.email && !u.email.includes('@phone.zoyotrip.local')) ? u.email : 'N/A',
                            phone: u.phone || 'N/A',
                            photoURL: u.avatar ? (u.avatar.startsWith('http') ? u.avatar : `${apiBase.replace('/api/v1', '')}/storage/${u.avatar}`) : null,
                            joinDate: u.created_at
                                ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                                : 'N/A'
                        });
                    }
                } else if (res.status === 401) {
                    localStorage.removeItem('auth_token');
                    setIsLoggedIn(false);
                    navigate('/login', { replace: true });
                }
            } catch (err) {
                console.error('Failed to fetch profile:', err);
            } finally {
                setLoadingProfile(false);
            }
        };

        fetchProfile();
    }, [navigate]);

    // Fetch real bookings & wallet data once logged in
    useEffect(() => {
        if (!isLoggedIn) return;

        const fetchDashboardData = async () => {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            const token = localStorage.getItem('auth_token');
            if (!token) { setLoadingData(false); return; }

            try {
                // Fetch bookings
                const bookingsRes = await fetch(`${apiBase}/bookings`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                });
                if (bookingsRes.ok) {
                    const bookingsData = await bookingsRes.json();
                    const bookings = bookingsData.data || [];
                    const today = new Date().toISOString().split('T')[0];

                    const activeCount = bookings.filter(b =>
                        b.status === 'confirmed' && b.check_out_date >= today
                    ).length;

                    setDashboardStats(prev => ({
                        ...prev,
                        totalBookings: bookings.length,
                        activeBookings: activeCount
                    }));

                    // Find the nearest upcoming booking (confirmed, check-in today or later)
                    const upcoming = bookings
                        .filter(b => b.check_in_date >= today && (b.status === 'confirmed' || b.status === 'pending'))
                        .sort((a, b) => new Date(a.check_in_date) - new Date(b.check_in_date))[0];

                    if (upcoming) {
                        const prop = upcoming.property;
                        const thumbnail = prop?.images?.find(img => img.category === 'hotel_thumbnail');
                        const imageUrl = thumbnail
                            ? `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${thumbnail.file_path}`
                            : (prop?.images?.[0] ? `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${prop.images[0].file_path}` : 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80');

                        setUpcomingBooking({
                            id: upcoming.booking_id,
                            hotelName: prop?.name || 'Hotel',
                            location: [prop?.city, prop?.state].filter(Boolean).join(', ') || 'India',
                            image: imageUrl,
                            checkIn: new Date(upcoming.check_in_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
                            checkOut: new Date(upcoming.check_out_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
                            status: upcoming.status.charAt(0).toUpperCase() + upcoming.status.slice(1),
                            amount: Number(upcoming.total_price) || 0
                        });
                    }
                }

                // Fetch wallet balance
                try {
                    const walletRes = await fetch(`${apiBase}/auth/wallet`, {
                        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                    });
                    if (walletRes.ok) {
                        const walletData = await walletRes.json();
                        setDashboardStats(prev => ({
                            ...prev,
                            walletBalance: walletData.data?.balance || 0
                        }));
                    }
                } catch (walletErr) {
                    console.warn('Wallet fetch failed:', walletErr);
                }
            } catch (err) {
                console.error('Failed to fetch dashboard data:', err);
            } finally {
                setLoadingData(false);
            }
        };

        fetchDashboardData();
    }, [isLoggedIn]);

    const handleLogout = async () => {
        try {
            localStorage.removeItem('auth_token');
            try { await auth.signOut(); } catch (e) { /* Firebase may not have a session */ }
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleCall = () => {
        window.location.href = "tel:+916261038929";
    };

    const menuItems = [
        {
            category: 'Account & Preferences',
            items: [
                { icon: Heart, label: 'Saved Hotels', action: () => navigate('/account/saved-hotels'), color: 'text-red-500' },
                { icon: Download, label: 'Download Our App', action: () => console.log('Download app'), color: 'text-blue-600' },
            ]
        },
        {
            category: 'Information',
            items: [
                { icon: Info, label: 'About Us', action: () => navigate('/about-us'), color: 'text-blue-500' },
                { icon: Shield, label: 'Privacy Policy', action: () => navigate('/privacy-policy'), color: 'text-gray-600' },
                { icon: FileText, label: 'Terms and Conditions', action: () => navigate('/terms-and-conditions'), color: 'text-gray-600' },
                { icon: Users, label: 'Guest Policy', action: () => navigate('/guest-policy'), color: 'text-orange-600' },
                { icon: XCircle, label: 'Cancellation Policy', action: () => navigate('/cancellation-policy'), color: 'text-red-600' },
            ]
        },
        {
            category: 'Resources & Support',
            items: [
                { icon: BookOpen, label: 'Blogs', action: () => navigate('/blogs'), color: 'text-indigo-600' },
                { icon: HelpCircle, label: 'FAQ', action: () => navigate('/faq'), color: 'text-yellow-600' },
                { icon: Phone, label: 'Call Us', action: handleCall, color: 'text-green-500' },
                { icon: Contact, label: 'Contact Us', action: () => navigate('/contact-us'), color: 'text-blue-500' },
            ]
        },
        {
            items: [
                { icon: LogOutIcon, label: 'Logout', action: () => handleLogout(), color: 'text-red-500' }
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 px-4 dashboard-container">
            <DefaultSEO title="My Account | ZoyoTrip" />
            <div className="max-w-4xl mx-auto pb-[100px]">
                {!isLoggedIn ? (
                    <div className="bg-white p-6 mb-6 rounded-xl shadow-sm">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Welcome!</h2>
                        <p className="text-gray-600 mb-6">Please login or create an account to access your profile and bookings.</p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => navigate('/login')}
                                className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                            >
                                <LogIn className="w-5 h-5 mr-2" />
                                Login or Create an Account
                            </button>
                        </div>
                    </div>
                ) : loadingProfile ? (
                    <div className="text-center py-20">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="text-muted mt-3">Loading profile...</p>
                    </div>
                ) : (
                    <>
                        {/* Profile Header (Flat & No Padding) */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between gap-3 flex-row text-left">
                                <div className="flex items-center gap-3">
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-50 rounded-full flex items-center justify-center border border-blue-100 overflow-hidden shadow-inner flex-shrink-0">
                                        {userProfile.photoURL && !imageError ? (
                                            <img
                                                src={userProfile.photoURL}
                                                alt="Profile"
                                                className="w-full h-full object-cover"
                                                onError={() => setImageError(true)}
                                            />
                                        ) : (
                                            <span className="text-2xl sm:text-3xl font-bold text-blue-600">
                                                {userProfile.name ? userProfile.name.charAt(0).toUpperCase() : 'U'}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <h2 className="text-[12px] sm:text-2xl font-bold text-gray-900 mb-0.5 whitespace-nowrap truncate max-w-[120px] sm:max-w-none">{userProfile.name}</h2>
                                        <div className="text-[10px] sm:text-sm text-gray-500 space-y-0.5">
                                            {userProfile.email !== 'N/A' && <p className="mb-0 leading-tight whitespace-nowrap truncate max-w-[120px] sm:max-w-none">{userProfile.email}</p>}
                                            {userProfile.phone !== 'N/A' && <p className="mb-0 leading-tight whitespace-nowrap truncate">{userProfile.phone}</p>}
                                            <p className="mb-0 text-[9px] sm:text-xs text-gray-400 mt-1">Joined {userProfile.joinDate}</p>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate('/account/edit')}
                                    className="flex items-center justify-center p-2 sm:px-4 sm:py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-semibold shadow-sm flex-shrink-0"
                                    title="Edit Profile"
                                >
                                    <Edit className="w-4 h-4 sm:me-1.5" />
                                    <span className="hidden sm:inline">Edit Profile</span>
                                </button>
                            </div>
                        </div>


                        {/* Recent / Upcoming Booking Detail (Flat & No Padding) */}
                        <div className="mb-6">
                            <div className="py-3 border-b border-gray-100 d-flex justify-content-between align-items-center mb-4">
                                <h3 className="text-md sm:text-lg font-bold text-gray-900 mb-0">Upcoming Booking</h3>
                                <button
                                    onClick={() => navigate('/account/bookings')}
                                    className="btn btn-link text-blue-600 p-0 d-flex align-items-center hover:text-blue-700 transition-colors"
                                    aria-label="View All Bookings"
                                >
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                            {loadingData ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary spinner-border-sm" role="status"></div>
                                    <p className="text-muted small mt-2 mb-0">Loading bookings...</p>
                                </div>
                            ) : upcomingBooking ? (
                                <div onClick={() => navigate(`/account/bookings/${upcomingBooking.id}`)} className="cursor-pointer transition-transform hover:scale-[1.01]">
                                    <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                                        <div className="w-full md:w-1/3 h-32 sm:h-40 rounded-xl overflow-hidden shadow-sm flex-shrink-0">
                                            <img src={upcomingBooking.image} alt={upcomingBooking.hotelName} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 flex flex-col justify-between">
                                            <div>
                                                <div className="flex justify-between items-start gap-2 mb-2">
                                                    <div>
                                                        <h4 className="text-md sm:text-lg font-bold text-gray-900 mb-1 leading-snug">{upcomingBooking.hotelName}</h4>
                                                        <p className="text-xs sm:text-sm text-gray-500 mb-0 d-flex align-items-center gap-1">
                                                            <MapPin className="w-3.5 h-3.5 text-gray-400" /> {upcomingBooking.location}
                                                        </p>
                                                    </div>
                                                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${upcomingBooking.status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`}>
                                                        {upcomingBooking.status}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-gray-400 font-semibold mt-1">Booking ID: {upcomingBooking.id}</p>
                                            </div>

                                            <div className="border-t border-gray-100 pt-3 mt-3 flex flex-wrap justify-between items-end gap-3">
                                                <div className="flex gap-4">
                                                    <div>
                                                        <span className="text-[9px] uppercase font-bold text-gray-400 block mb-0.5">Check-In</span>
                                                        <span className="text-xs font-bold text-gray-800 d-flex align-items-center gap-1">
                                                            <Calendar className="w-3.5 h-3.5 text-blue-500" /> {upcomingBooking.checkIn}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-[9px] uppercase font-bold text-gray-400 block mb-0.5">Check-Out</span>
                                                        <span className="text-xs font-bold text-gray-800 d-flex align-items-center gap-1">
                                                            <Calendar className="w-3.5 h-3.5 text-indigo-500" /> {upcomingBooking.checkOut}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[9px] uppercase font-bold text-gray-400 block mb-0.5">Total Paid</span>
                                                    <span className="text-base sm:text-lg font-extrabold text-blue-600">₹{upcomingBooking.amount.toLocaleString('en-IN')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                    <p className="text-gray-500 text-sm mb-1">No upcoming bookings</p>
                                    <button onClick={() => navigate('/hotels')} className="btn btn-sm btn-outline-primary rounded-pill mt-1">
                                        Explore Hotels
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Menu Links */}
                        <div className="space-y-4">
                            {menuItems.map((category, categoryIndex) => (
                                <div key={categoryIndex} className="overflow-hidden">
                                    {category.category && (
                                        <div className="px-1 py-2 mb-1">
                                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0">{category.category}</h3>
                                        </div>
                                    )}
                                    <div className="divide-y divide-gray-100">
                                        {category.items.map((item, itemIndex) => {
                                            const IconComponent = item.icon;
                                            return (
                                                <button
                                                    key={itemIndex}
                                                    onClick={item.action}
                                                    className="w-full flex items-center justify-between py-3 sm:py-3.5 hover:bg-gray-50 transition-colors duration-150 text-left border-0 border-b border-gray-100/50"
                                                    style={{ background: 'none' }}
                                                >
                                                    <div className="flex items-center">
                                                        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center mr-3 flex-shrink-0 border border-gray-100 shadow-sm">
                                                            <IconComponent className={`w-4 h-4 ${item.color}`} />
                                                        </div>
                                                        <span className="text-gray-700 font-semibold text-sm">{item.label}</span>
                                                    </div>
                                                    <ArrowRight className="w-4 h-4 text-gray-300" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Dynamic CSS injection to fix navbar overlap on desktop view */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @media (min-width: 768px) {
                    .dashboard-container {
                        padding-top: 100px !important;
                    }
                }
                @media (max-width: 767.98px) {
                    .dashboard-container {
                        padding-top: 20px !important;
                    }
                }
            `}} />
        </div>
    );
};

export default AccountDetailPage;